import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Grid } from "antd";
import { capitalize } from "lodash";
import { useTranslation } from "react-i18next";

const { useBreakpoint } = Grid;

interface RadarChartProps {
  titulo?: string;
  cor?: string[];
  series: any;
  categories: any;
  width?: number;
  legend?: boolean;
  tooltip?: any;
  height?: number;
  textColor?: string;
  labelFontSize?: string;
  containerWidth?: string;
  min?: number;
  max?: number;
  markerSize?: number;
  strokeWidth?: number;
  strokeColor?: string;
  names?: string[];
  showTooltip?: boolean;
  useI18nLabel?: boolean;
  animation?: boolean;
  legendStyle?: any;
  tickAmount?: number;
  radarOffsetX?: number;
  radarOffsetY?: number;
  multiplyBy100?: boolean;
  polygonProps: {
    strokeColors?: string | string[];
    connectorColors?: string | string[];
    strokeWidth?: string;
    fillColors?: string[];
  };
}
const RadarChart: React.FC<RadarChartProps> = (props) => {
  const {
    titulo,
    cor,
    series,
    categories,
    width,
    tooltip,
    legend = true,
    labelFontSize,
    height = "auto",
    textColor = "#fff",
    containerWidth = "100%",
    min = 0,
    max = 1,
    markerSize = 6,
    strokeWidth = 2,
    strokeColor,
    names,
    showTooltip = true,
    useI18nLabel = true,
    multiplyBy100 = false,
    animation = true,
    legendStyle,
    tickAmount = 4,
    radarOffsetX = 0,
    radarOffsetY = 0,
    polygonProps = {
      strokeColors: [textColor, textColor, textColor, textColor],
      connectorColors: textColor,
      fillColors: ["transparent"],
    },
  } = props;
  const { t } = useTranslation("radar");

  const seriesWithNames = names
    ? series.map((item: any, index: number) => ({
        name: names[index],
        ...item,
      }))
    : series;

  const breakpoint = useBreakpoint();
  const annotations: ApexAnnotations = {};
  const options: ApexOptions = {
    chart: {
      background: "none",
      toolbar: {
        show: false,
      },
      animations: {
        enabled: animation,
      },
    },
    fill: {
      type: "none",
    },
    title: {
      text: titulo,
      style: {
        color: textColor,
      },
    },
    plotOptions: {
      radar: {
        size: width ? width : breakpoint.xxl ? 250 : breakpoint.lg ? 180 : 40,
        polygons: {
          strokeColors: polygonProps.strokeColors,
          connectorColors: polygonProps.connectorColors,
          strokeWidth: polygonProps.strokeWidth,
          fill: {
            colors: polygonProps.fillColors,
          },
        },
        offsetX: radarOffsetX,
        offsetY: radarOffsetY,
      },
    },
    yaxis: {
      show: false,
      tickAmount: tickAmount,
      max: max,
    },
    colors: cor,
    xaxis: {
      categories: categories?.map((item: string) =>
        capitalize(
          useI18nLabel
            ? item === "stage"
              ? t("pages.CareerDiagnose.stagePresence")
              : t(`pages.CareerDiagnose.${item}`)
            : item
        )
      ),
      min: min,
      labels: {
        offsetY: 5,
        style: {
          fontSize: labelFontSize
            ? labelFontSize
            : breakpoint.xxl
            ? "18px"
            : "12px",
          colors: categories?.map(() => textColor),
        },
      },
    },
    markers: {
      size: markerSize,
      strokeWidth: strokeWidth,
      strokeColors: strokeColor ? strokeColor : textColor,
    },
    annotations: annotations,
    legend: {
      show: legend,
      showForSingleSeries: true,
      fontSize: "16px",
      labels: {
        colors: textColor,
      },
      ...legendStyle,
    },
    tooltip: {
      enabled: showTooltip,
      custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
        const title = categories[dataPointIndex];
        const titleI18n = useI18nLabel
          ? title === "stage"
            ? t("pages.CareerDiagnose.stagePresence")
            : t(`pages.CareerDiagnose.${title}`)
          : title; //remover quando passar esse i18n para o component
        const tooltipInfo = tooltip?.find(
          (point: any) => point.name === title
        ) || {
          text: t(`pages.CareerDiagnose.${title}Text`),
        };
        const val = series[seriesIndex][dataPointIndex];
        const color = w.config.colors[seriesIndex];
        const serieName = w.config.series[seriesIndex].name;
        return `
          <div class="apexcharts-tooltip-title" style="font-size:12px">${titleI18n}</div>
          <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
            <span class="apexcharts-tooltip-marker" style="background-color: ${color}"></span>
            <div class="apexcharts-tooltip-text" style="font-size:12px">
            <div class="apexcharts-tooltip-y-group">
              <span class="apexcharts-tooltip-text-y-label">${serieName}: </span>
              <span class="apexcharts-tooltip-text-y-value">${
                title === "tempo"
                  ? `${Math.round(tooltipInfo.bpm[seriesIndex] || 0)} BPM`
                  : Math.round(multiplyBy100 ? val * 100 : val)
              }</span>
            </div>
              ${
                (tooltipInfo &&
                  `<div class="apexcharts-tooltip-goals-group">
                    <span class="apexcharts-tooltip-text-goals-label">${tooltipInfo.text} </span>                         
                </div>`) ||
                ""
              }                       
          </div>
        </div>
      `;
      },
    },
  };

  return (
    <div style={{ color: "#000" }}>
      <Chart
        series={seriesWithNames}
        options={options}
        type="radar"
        height={height}
        width={containerWidth}
      />
    </div>
  );
};

export default RadarChart;
